/**
 * Time Tracking API — Step 17
 */
import { baseApi } from './baseApi';

const timeApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // ── Timer ──
    startTimer: builder.mutation({
      query: (body) => ({ url: '/time/timer/start', method: 'POST', body }),
      invalidatesTags: ['TimeEntry'],
    }),
    pauseTimer: builder.mutation({
      query: () => ({ url: '/time/timer/pause', method: 'POST' }),
    }),
    resumeTimer: builder.mutation({
      query: () => ({ url: '/time/timer/resume', method: 'POST' }),
    }),
    stopTimer: builder.mutation({
      query: () => ({ url: '/time/timer/stop', method: 'POST' }),
      invalidatesTags: ['TimeEntry'],
    }),
    discardTimer: builder.mutation({
      query: () => ({ url: '/time/timer', method: 'DELETE' }),
    }),
    getTimerStatus: builder.query({
      query: () => '/time/timer/status',
    }),

    // ── Entries ──
    listTimeEntries: builder.query({
      query: (params) => ({ url: '/time/entries', params }),
      providesTags: ['TimeEntry'],
    }),
    createTimeEntry: builder.mutation({
      query: (body) => ({ url: '/time/entries', method: 'POST', body }),
      invalidatesTags: ['TimeEntry'],
    }),
    updateTimeEntry: builder.mutation({
      query: ({ entryId, ...body }) => ({ url: `/time/entries/${entryId}`, method: 'PUT', body }),
      invalidatesTags: ['TimeEntry'],
    }),
    deleteTimeEntry: builder.mutation({
      query: (entryId) => ({ url: `/time/entries/${entryId}`, method: 'DELETE' }),
      invalidatesTags: ['TimeEntry'],
    }),

    // ── Weekly grid ──
    getWeeklyGrid: builder.query({
      query: (params) => ({ url: '/time/weekly', params }),
      providesTags: ['TimeEntry'],
    }),
    getWeeklyProjectRows: builder.query({
      query: (params) => ({ url: '/time/weekly/rows', params }),
      providesTags: ['TimeEntry'],
    }),

    // ── Timesheets ──
    listTimesheets: builder.query({
      query: (params) => ({ url: '/time/timesheets', params }),
      providesTags: ['Timesheet'],
    }),
    submitTimesheet: builder.mutation({
      query: (body) => ({ url: '/time/timesheets/submit', method: 'POST', body }),
      invalidatesTags: ['Timesheet', 'TimeEntry'],
    }),
    approveTimesheet: builder.mutation({
      query: ({ timesheetId, note }) => ({
        url: `/time/timesheets/${timesheetId}/approve`, method: 'POST', body: { note },
      }),
      invalidatesTags: ['Timesheet'],
    }),
    rejectTimesheet: builder.mutation({
      query: ({ timesheetId, note }) => ({
        url: `/time/timesheets/${timesheetId}/reject`, method: 'POST', body: { note },
      }),
      invalidatesTags: ['Timesheet'],
    }),
    recallTimesheet: builder.mutation({
      query: (timesheetId) => ({ url: `/time/timesheets/${timesheetId}/recall`, method: 'POST' }),
      invalidatesTags: ['Timesheet'],
    }),

    // ── Reports ──
    getTimeSummary: builder.query({
      query: (params) => ({ url: '/time/summary', params }),
      providesTags: ['TimeEntry'],
    }),
    getTimeDashboard: builder.query({
      query: () => '/time/dashboard',
      providesTags: ['TimeEntry'],
    }),

    // ── Overtime Summary ──
    getOvertimeSummary: builder.query({
      query: (params) => ({ url: '/time/overtime', params }),
      providesTags: ['TimeEntry'],
    }),

    // ── Idle Time Detection ──
    detectIdleTime: builder.query({
      query: (params) => ({ url: '/time/idle', params }),
    }),

    // ── Calendar → Time Entry Suggestions ──
    getCalendarTimeSuggestions: builder.query({
      query: (params) => ({ url: '/time/calendar-suggest', params }),
    }),

    // ── Pending Approval Reminders ──
    getPendingApprovalReminders: builder.query({
      query: (params) => ({ url: '/time/reminders/pending', params }),
      providesTags: ['Timesheet'],
    }),

    // ── Billing rates ──
    getBillingRates: builder.query({
      query: () => '/time/rates',
    }),
    setBillingRate: builder.mutation({
      query: (body) => ({ url: '/time/rates', method: 'POST', body }),
      invalidatesTags: ['TimeEntry'],
    }),
  }),
});

export const {
  useStartTimerMutation, usePauseTimerMutation, useResumeTimerMutation,
  useStopTimerMutation, useDiscardTimerMutation, useGetTimerStatusQuery,
  useListTimeEntriesQuery, useCreateTimeEntryMutation, useUpdateTimeEntryMutation, useDeleteTimeEntryMutation,
  useGetWeeklyGridQuery, useGetWeeklyProjectRowsQuery,
  useListTimesheetsQuery, useSubmitTimesheetMutation, useApproveTimesheetMutation, useRejectTimesheetMutation, useRecallTimesheetMutation,
  useGetTimeSummaryQuery, useGetTimeDashboardQuery,
  // New
  useGetOvertimeSummaryQuery, useDetectIdleTimeQuery,
  useGetCalendarTimeSuggestionsQuery, useGetPendingApprovalRemindersQuery,
  useGetBillingRatesQuery, useSetBillingRateMutation,
} = timeApi;
